/**
 * Platform-native colour + typography tokens for the ad-preview system.
 *
 * Source: Figma file `Dl0gE7lH27N7x7OJb0DFBw`, "Components" page (`28:13800`),
 * Color frame (`28:13893`) and Typography frame (`28:13801`).
 * See `docs/planning/GOAL-AD-PREVIEW-SYSTEM-2026-08-21.md` §2 for the ruling
 * this file exists to satisfy.
 *
 * ── WHY THIS FILE EXISTS (the licensed exception) ──────────────────────────
 * Every other surface in this app pulls color from Mora's semantic tokens
 * (`bg-card`, `text-foreground`, …) so it inherits Mora's active theme. An ad
 * preview must NOT do that: Facebook's card is Facebook-grey in both Mora
 * themes, Instagram's dark card is pure black in both Mora themes, etc. The
 * platform's own appearance IS the information — a preview tinted with
 * Mora's palette is a preview that lies about what the ad will look like.
 * This is the same reasoning that already licenses full-colour platform
 * brand icons and `GradientChoiceCard`; see hard rule 1's carve-out.
 *
 * Consuming code MUST treat every value below as a NAMED TOKEN — read a
 * property off `PLATFORM_SKINS`, never copy a hex literal into a component.
 * A hex literal in className is also mechanically blocked by the
 * `no-restricted-syntax` hex-in-className lint rule in `eslint.config.js`;
 * this file itself is exempt because — even now — it holds no literal hex at
 * all (see below), so the exemption is belt-and-suspenders, not load-bearing.
 *
 * ── WHERE THE HEX ACTUALLY LIVES (superseding this file's earlier reasoning) ──
 * An earlier version of this file argued the hex literals should stay HERE,
 * in TS, specifically because "these values are never read through
 * Tailwind's generated utilities… and they are selected in JS per render,
 * not cascaded via `.dark`" — duplicating them into CSS would supposedly
 * create a second source of truth.
 *
 * That reasoning correctly ruled out the naive fix (a `.dark`-class
 * cascade), but conflated "not cascaded via `.dark`" with "can't live in
 * CSS at all." Those are different constraints. The fix: give every
 * platform × theme × role its own uniquely-named CSS custom property —
 * `--platform-<platform>-<theme>-<role>`, e.g.
 * `--platform-facebook-dark-card-background` — defined ONCE in a real
 * `:root` block in `src/index.css` (never redefined under `.dark`, because
 * the theme is already baked into the variable's NAME, not its cascade
 * position). `PLATFORM_SKINS` below now holds `var(--platform-…)` strings
 * that resolve those names, not hex. This is the exact pattern this file's
 * own `metaAdLinkBandBackground()`-adjacent precedent already used for the
 * one non-`PLATFORM_SKINS` platform colour that predates this migration —
 * `--platform-facebook-link-band-light`/`-dark` in `src/index.css` — now
 * applied to the rest of the role set instead of treating that pair as a
 * one-off. There is exactly one source of truth (the CSS block); this file
 * only types and re-exports references to it, so nothing can drift.
 *
 * 🔴 Tailwind v4 trap this migration deliberately avoids: an `@theme` block
 * PRUNES any custom property with no generated static utility referencing
 * it. These variables are only ever read through an inline `style={{ … }}`
 * `var()` lookup (see "HOW A CONSUMER APPLIES A SKIN" below), so they live
 * in a real `:root` selector in `src/index.css`, same as `--object-tile-*`
 * and `--platform-facebook-link-band-*` there — not in `@theme`. Putting
 * them in `@theme` compiles cleanly, passes `tsc` and lint, and then
 * silently resolves to nothing at runtime; see that file's own comment on
 * `--object-glyph-*` for the incident this trap previously caused.
 *
 * ── HOW A CONSUMER APPLIES A SKIN (mechanism decision, unchanged) ──────────
 * Which skin variant renders is STILL a per-instance JS choice — "Mora
 * light ⇒ dark:false, Mora dark ⇒ dark:true" — not an ambient CSS cascade.
 * That matters because a style-guide page needs to show BOTH the light and
 * dark variant of a platform side by side, regardless of which theme Mora
 * itself is in. Moving the hex into CSS does not change this: the consumer
 * still does `PLATFORM_SKINS[platform][resolvedTheme]` in JS exactly as
 * before — the only difference is that `skin.cardBackground` now evaluates
 * to the STRING `"var(--platform-facebook-dark-card-background)"` instead
 * of `"#242527"`. Because the variable name itself already encodes which
 * variant it is, two `<div>`s on the same page reading two different
 * `PLATFORM_SKINS[...][...]` selections resolve to two different CSS custom
 * properties — there is no shared/cascaded variable name for `.dark` (or
 * anything else) to collide on, so side-by-side light/dark rendering keeps
 * working with zero DOM-level scoping (no `data-skin` wrapper attribute
 * needed): the "element scoping" the problem calls for is achieved by
 * variable NAMING rather than by a scoped selector, and the two behave
 * identically from the consumer's point of view — apply via inline
 * `style` (`style={{ backgroundColor: skin.cardBackground }}`) — NEVER via
 * a Tailwind class holding a hex or named color, which the lint rule above
 * blocks anyway. This repo's hard-rule-4 exception for inline style
 * ("dynamic CSS vars") covers exactly this case: the value is data-driven,
 * not a static design decision baked into a class.
 */

export type PlatformSkinKey =
  'facebook' | 'instagram' | 'twitter' | 'google-shopping' | 'google-search' | 'linkedin';

export type PlatformSkinTheme = 'light' | 'dark';

/**
 * The role vocabulary every platform × theme skin fills. Kept to the roles
 * that were independently confirmed, by reading real template usage (not
 * just the Color frame's swatch list), across ALL FIVE platforms:
 *
 *   - `pageBackground` — the surface behind the card (visible on Google's
 *     card-in-a-frame templates; equals `cardBackground` for the
 *     full-bleed Facebook/Instagram/Twitter templates).
 *   - `cardBackground` — the post/card surface itself.
 *   - `cardBorder` — the card's divider/border/hairline color.
 *   - `primaryText` — headline, name, price, body copy's most prominent role.
 *   - `secondaryText` — timestamp, meta, engagement counts, disclaimers.
 *   - `ctaBackground` — the fill behind the primary action (Shop Now /
 *     Follow). For platforms whose CTA is a bare link with no button fill,
 *     this equals `cardBackground` (i.e. "no fill") — see per-platform notes.
 *   - `ctaText` — the text color on top of `ctaBackground`.
 *
 * Two more roles are confirmed for SOME platforms only and are therefore
 * optional — leaving them `undefined` where a platform genuinely has no
 * such role is accurate, not a gap:
 *
 *   - `linkText` — a link/accent hue distinct from `ctaText` (Instagram's
 *     blue "Shop Now" link, Google's blue product-title/headline link).
 *     Facebook and Twitter have no such hue in this file: their only
 *     brand-color signal is the CTA pill itself.
 *   - `merchantText` — Google Shopping's second accent for the merchant/
 *     company name line (green in light, blue in dark) — a role no other
 *     platform's sampled template carries.
 *
 * Per-role sourcing notes and confidence caveats (Google Search's
 * unresolved-variable transcription, LinkedIn's estimated/web-sourced
 * values) live as comments on the CSS custom properties in `src/index.css`
 * now, next to the literals they annotate — not duplicated here.
 */
export interface PlatformSkin {
  pageBackground: string;
  cardBackground: string;
  cardBorder: string;
  primaryText: string;
  secondaryText: string;
  ctaBackground: string;
  ctaText: string;
  linkText?: string;
  merchantText?: string;
  /**
   * The filled star in Google Shopping's rating row. Measured off the Figma
   * renders of `28:13044`/`28:13043` rather than declared as a named style —
   * the rating node is a component instance, so its fill does not appear in
   * the frame's own metadata. The EMPTY star has no role of its own: it is
   * `cardBorder` in both themes, which is what the renders show exactly
   * (#dfe1e5 light, #979797 dark).
   */
  star?: string;
}

type PlatformSkinSet = Record<PlatformSkinTheme, PlatformSkin>;

/**
 * Builds the `var(--platform-<platform>-<theme>-<role>)` reference string
 * for one role. Centralised so the naming convention (kebab-case platform
 * key, kebab-case role) is enforced in exactly one place rather than
 * hand-typed per platform per theme per role — a hand-typed table of ~90
 * strings is exactly the shape of thing that drifts from `src/index.css`'s
 * actual variable names one keystroke at a time.
 */
function skinVar(platform: PlatformSkinKey, theme: PlatformSkinTheme, role: string): string {
  return `var(--platform-${platform}-${theme}-${role})`;
}

function buildSkin(
  platform: PlatformSkinKey,
  theme: PlatformSkinTheme,
  roles: readonly string[],
): Record<string, string> {
  return Object.fromEntries(roles.map((role) => [role, skinVar(platform, theme, role)]));
}

const CORE_ROLES = [
  'page-background',
  'card-background',
  'card-border',
  'primary-text',
  'secondary-text',
  'cta-background',
  'cta-text',
] as const;

/** Maps the kebab-case CSS role name to the camelCase `PlatformSkin` field. */
function toPlatformSkin(
  vars: Record<string, string>,
  extra: Partial<{ linkText: string; merchantText: string; star: string }> = {},
): PlatformSkin {
  return {
    pageBackground: vars['page-background'],
    cardBackground: vars['card-background'],
    cardBorder: vars['card-border'],
    primaryText: vars['primary-text'],
    secondaryText: vars['secondary-text'],
    ctaBackground: vars['cta-background'],
    ctaText: vars['cta-text'],
    ...extra,
  };
}

function skin(
  platform: PlatformSkinKey,
  theme: PlatformSkinTheme,
  options: { linkText?: boolean; merchantText?: boolean; star?: boolean } = {},
): PlatformSkin {
  const core = buildSkin(platform, theme, CORE_ROLES);
  const extra: Partial<{ linkText: string; merchantText: string; star: string }> = {};
  if (options.linkText) extra.linkText = skinVar(platform, theme, 'link-text');
  if (options.merchantText) extra.merchantText = skinVar(platform, theme, 'merchant-text');
  if (options.star) extra.star = skinVar(platform, theme, 'star');
  return toPlatformSkin(core, extra);
}

export const PLATFORM_SKINS: Record<PlatformSkinKey, PlatformSkinSet> = {
  // Facebook has no distinct link/accent hue — the CTA is a neutral grey
  // pill in both themes, so `linkText` is intentionally omitted.
  facebook: {
    light: skin('facebook', 'light'),
    dark: skin('facebook', 'dark'),
  },

  instagram: {
    light: skin('instagram', 'light', { linkText: true }),
    dark: skin('instagram', 'dark', { linkText: true }),
  },

  // Twitter/X has no distinct link/accent hue — the CTA pill is the only
  // brand-color signal, so `linkText` is intentionally omitted.
  twitter: {
    light: skin('twitter', 'light'),
    dark: skin('twitter', 'dark'),
  },

  'google-shopping': {
    light: skin('google-shopping', 'light', { linkText: true, merchantText: true, star: true }),
    // Dark mode has no `linkText`: the product title is not styled as a
    // link there — the blue role moves to `merchantText` instead. See
    // `src/index.css`'s comment block for the confirmed role-swap detail.
    dark: skin('google-shopping', 'dark', { merchantText: true, star: true }),
  },

  'google-search': {
    light: skin('google-search', 'light', { linkText: true }),
    dark: skin('google-search', 'dark', { linkText: true }),
  },

  linkedin: {
    light: skin('linkedin', 'light', { linkText: true }),
    dark: skin('linkedin', 'dark', { linkText: true }),
  },
};

/**
 * Typography roles from the Typography frame (`28:13801`). All three are
 * Roboto in the source of truth. A handful of individual template nodes
 * render in Lato instead (Instagram-dark's "Shop Now" pill, Google Search's
 * "Ad" label) — those read as file inconsistencies (most likely a
 * copy-paste from a different component library) rather than a fourth
 * declared type role, so they are not represented here. Phase-2 template
 * authors should use Roboto throughout and treat any Lato sighting in the
 * reference code as noise.
 */
export const PLATFORM_TYPOGRAPHY = {
  body: { fontFamily: 'Roboto', fontWeight: 400, fontSize: 13 },
  caption: { fontFamily: 'Roboto', fontWeight: 400, fontSize: 12 },
  bold: { fontFamily: 'Roboto', fontWeight: 600, fontSize: 13 },
} as const satisfies Record<
  'body' | 'caption' | 'bold',
  {
    fontFamily: string;
    fontWeight: number;
    fontSize: number;
  }
>;

export type PlatformTypographyRole = keyof typeof PLATFORM_TYPOGRAPHY;
